import { DateTime } from 'luxon';
import { useState } from 'react';

import { artiaProtectedApi } from '@/lib/api-client';
import { BoardMeetingFilterData } from '../api';

export const useBoardMeetingExport = () => {
  const [isExporting, setIsExporting] = useState(false);

  const exportBoardMeetings = async (filters?: BoardMeetingFilterData) => {
    setIsExporting(true);
    
    try {
      // Generate filename with current date
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `board-meetings-export-${dateStr}.csv`;

      // Prepare query parameters for filtering
      const queryParams = new URLSearchParams();
      
      if (filters) {
        // Add states or pools filter
        if (filters.statesOrPools && filters.statesOrPools.length > 0) {
          filters.statesOrPools.forEach(item => queryParams.append('statesOrPools', item.id));
        }
        
        // Add drugs filter
        if (filters.drugs && filters.drugs.length > 0) {
          filters.drugs.forEach(drug => queryParams.append('drugs', drug.id.toString()));
        }
        
        // Add classifications filter
        if (filters.classes && filters.classes.length > 0) {
          filters.classes.forEach(classification => queryParams.append('classes', classification.id.toString()));
        }
        
        // Add meeting types filter
        if (filters.meetingTypes && filters.meetingTypes.length > 0) {
          filters.meetingTypes.forEach(type => queryParams.append('meetingTypes', type.id.toString()));
        }
        
        // Add date range filters
        if (filters.dateRangeBegin) {
          queryParams.append('dateRangeBegin', filters.dateRangeBegin);
        }
        
        if (filters.dateRangeEnd) {
          queryParams.append('dateRangeEnd', filters.dateRangeEnd);
        }
      }

      const queryString = queryParams.toString();
      const apiUrl = `/board-meetings/export/csv${queryString ? `?${queryString}` : ''}`;

      // Check if showSaveFilePicker is available (modern browsers)
      if ('showSaveFilePicker' in window) {
        try {
          const fileHandle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [
              {
                description: 'CSV files',
                accept: {
                  'text/csv': ['.csv'],
                },
              },
            ],
          });

          // Make the API call to get the CSV data
          const response = await artiaProtectedApi.get(apiUrl, {
            responseType: 'blob'
          });

          // Write the blob to the selected file
          const writable = await fileHandle.createWritable();
          await writable.write(response.data);
          await writable.close();

          return { success: true, method: 'save-picker' };
        } catch (error) {
          // User cancelled the save dialog or other error
          if ((error as Error).name === 'AbortError') {
            return { success: false, cancelled: true };
          }
          // Fall through to traditional download method
          console.warn('Save picker failed, falling back to download:', error);
        }
      }

      // Fallback to traditional download method
      const response = await artiaProtectedApi.get(apiUrl, {
        responseType: 'blob'
      });

      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      return { success: true, method: 'download' };
    } catch (error) {
      console.error('Board meeting export error:', error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportBoardMeetings,
    isExporting
  };
};
